export const ROUTES = {
  /** Public sign-in. Query: `workspace=<slug>`, or `tenantId=<uuid>` when coming from owner invite. */
  LOGIN:         '/login',
  /** Public owner-invite deep link: `tenantId` + `inviteId` query params. */
  OWNER_INVITE:  '/owner-invite',
  /** Owner activation with sequential ToS + Privacy consent, then password. */
  ACTIVATE:      '/activate',
  FORBIDDEN:     '/403',
  NOT_FOUND:     '/404',
  EDITORIAL:     '/editorial',
  /** Mode 2: manual post creation from a brief. */
  EDITORIAL_CREATE: '/editorial/create',
  /** Workspace users (tenant view) — Admin / Editor (spec §4). */
  EDITORIAL_TEAM: '/editorial/team',
  /** Workspace admins — register RSS URLs for ingestion (`POST /ingest/url`). */
  EDITORIAL_RSS_FEEDS: '/editorial/rss-feeds',
  /** Content Sources — Add source wizard (ADD-01/02, spec v1.1). */
  EDITORIAL_FEED_SOURCES_ADD: '/editorial/feed-sources/add',
  /** Content Sources — Add source wizard, ADD-03 Fields (read-only, RSS variant). */
  EDITORIAL_FEED_SOURCES_FIELDS: (id = ':id') => `/editorial/feed-sources/${id}/fields`,
  /** Content Sources — Add source wizard, ADD-05 Check (13 checks + activation). */
  EDITORIAL_FEED_SOURCES_CHECK: (id = ':id') => `/editorial/feed-sources/${id}/check`,
  /** Content Sources — DET source detail (overview/edit/mapping/audit log). */
  EDITORIAL_FEED_SOURCES_DETAIL: (id = ':id') => `/editorial/feed-sources/${id}`,
  /** Content Sources — M3 coverage grid (category x language). */
  EDITORIAL_FEED_SOURCES_COVERAGE: '/editorial/feed-sources/coverage',
  /** Workspace Admin / Owner — compare Bing vs GDELT vs Google Trends cluster feeds (SP-056). */
  EDITORIAL_TREND_SIGNALS: '/editorial/admin/trend-signals',
  /** Editors — current web-search trends and which became agent picks. */
  EDITORIAL_WEB_TRENDS: '/editorial/web-trends',
  /** Workspace Admin only — JioNews headlines partner credentials (`PUT /api/v1/integrations/headlines/config`). */
  EDITORIAL_HEADLINES_INTEGRATION: '/editorial/settings/headlines-integration',
  /** Quick Cards — block-based preset builder (fast, no free canvas). Separate from Card Studio. */
  EDITORIAL_SOCIAL_CARDS: '/editorial/social-cards',
  /** Card Studio — primary Konva canvas editor. Templates loaded here. Direct publish flow. */
  CARD_STUDIO: '/editorial/card-studio',
  /** Template Studio (Owner/Admin) — TTS-02 Template Library. */
  TEMPLATE_STUDIO: '/editorial/template-studio',
  /** Template Studio — TTS-03 Create Template (starting point). */
  TEMPLATE_STUDIO_NEW: '/editorial/template-studio/new',
  /** Template Studio — TTS-03B Duplicate Existing Template. */
  TEMPLATE_STUDIO_DUPLICATE: '/editorial/template-studio/new/duplicate',
  /** Template Studio — TTS-03C Import from Figma. */
  TEMPLATE_STUDIO_FIGMA: '/editorial/template-studio/new/figma',
  /** Figma OAuth popup landing (API callback redirects here). */
  TEMPLATE_STUDIO_FIGMA_CONNECTED: '/editorial/template-studio/figma/connected',
  /** Template Studio — TTS-03A Upload Design. */
  TEMPLATE_STUDIO_UPLOAD: '/editorial/template-studio/new/upload',
  /** Template Studio — TTS-04 Canvas Selection ("Create new" path). */
  TEMPLATE_STUDIO_CANVAS: '/editorial/template-studio/new/canvas',
  /** Template Studio — TTS-05 editor for a not-yet-saved template (canvas passed in router state). */
  TEMPLATE_STUDIO_NEW_EDITOR: '/editorial/template-studio/new/editor',
  /** Older template links; redirects to the editor. */
  TEMPLATE_STUDIO_ITEM: (id = ':id') => `/editorial/template-studio/${id}`,
  /** Template Studio — TTS-06 Mapping & Naming. */
  TEMPLATE_STUDIO_MAP: (id = ':id') => `/editorial/template-studio/${id}/map`,
  /** Template Studio — TTS-07 Test Preview. */
  TEMPLATE_STUDIO_PREVIEW: (id = ':id') => `/editorial/template-studio/${id}/preview`,
  /** Template Studio — TTS-05 editor entry for an existing template. */
  TEMPLATE_STUDIO_EDIT: (id = ':id') => `/editorial/template-studio/${id}/edit`,
  EDITORIAL_STORY:     (id = ':id') => `/editorial/stories/${id}`,
  EDITORIAL_HEADLINES: (id = ':id') => `/editorial/stories/${id}/headlines`,
  CARD_EDITOR:         '/editorial/card-editor',
  SEARCH:        '/editorial/search',
  PUBLISH_QUEUE: '/editorial/publish-queue',
  /** SP-087 — month/week calendar list (`ContentCalendarPage`). */
  CONTENT_CALENDAR: '/calendar',
  /** Posted content dashboard (B-28) — list route matches `App.tsx`. */
  POSTED_CONTENT: '/calendar/posted',
  /** SP-024: legacy alias — same route as Scheduled (flat list). */
  CALENDAR: '/editorial/publish-queue',
  ANALYTICS:     '/analytics',
  ADMIN:         '/admin',
  ADMIN_LOGIN:   '/admin/login',
  ADMIN_PLANS:   '/admin/plans',
  ADMIN_TENANTS: '/admin/tenants',
  /** SuperAdmin — create-tenant wizard (redirects to first step). */
  ADMIN_TENANTS_NEW: '/admin/tenants/new',
  /** SuperAdmin — create-tenant wizard step (`details` | `plan` | `languages` | `refresh` | `owner` | `confirm`). */
  ADMIN_TENANTS_NEW_STEP: (step = ':step') => `/admin/tenants/new/${step}`,
  ADMIN_USERS:   '/admin/users',
  ADMIN_AUDIT:   '/admin/audit',
  /** Super Admin — platform settings (Legal documents, etc.). */
  ADMIN_SETTINGS: '/admin/settings',
  /** Platform OAuth connections — owner/admin manage social platform tokens. */
  SETTINGS_INTEGRATIONS: '/settings/integrations',
} as const;

// spec §4: SuperAdmin / Owner / Admin / Editor — no Viewer
const ROLE_ORDER = { SuperAdmin: 5, Owner: 4, Admin: 3, Editor: 2 } as const;

export function maxRole(a: keyof typeof ROLE_ORDER, b: keyof typeof ROLE_ORDER): keyof typeof ROLE_ORDER {
  return ROLE_ORDER[a] >= ROLE_ORDER[b] ? a : b;
}

/**
 * After sign-in: honor `returnTo` only when it matches the user's area (e.g. `/admin/*` for platform admins).
 * Otherwise SuperAdmin/Admin were always bounced back to `/editorial` after hitting that route while logged out.
 */
export function postLoginRedirectPath(
  role: 'SuperAdmin' | 'Owner' | 'Admin' | 'Editor',
  returnTo?: string | null,
): string {
  /** Platform dashboard is SuperAdmin-only. `Admin` here maps API workspace owner/admin — not platform ops. */
  const isPlatformSuperAdmin = role === 'SuperAdmin';
  const safeReturnTo = sanitizeReturnTo(returnTo);
  if (safeReturnTo && safeReturnTo.startsWith(ROUTES.OWNER_INVITE)) {
    return safeReturnTo;
  }
  if (safeReturnTo && safeReturnTo.startsWith(ROUTES.ACTIVATE)) {
    return safeReturnTo;
  }
  if (safeReturnTo) {
    if (isPlatformSuperAdmin && safeReturnTo.startsWith('/admin')) return safeReturnTo;
    if (!isPlatformSuperAdmin && !safeReturnTo.startsWith('/admin')) return safeReturnTo;
  }
  if (isPlatformSuperAdmin) return ROUTES.ADMIN;
  return ROUTES.EDITORIAL;
}

/** Ignore error/auth pages so reactivate → login never restores a stuck `/403`. */
function sanitizeReturnTo(returnTo?: string | null): string | null {
  if (!returnTo) return null;
  const path = returnTo.split('?')[0]?.split('#')[0] || '';
  if (!path || path === ROUTES.LOGIN) return null;
  if (path === ROUTES.FORBIDDEN || path === ROUTES.NOT_FOUND) return null;
  if (path.startsWith('/auth/')) return null;
  return returnTo;
}
